import {ComplexFormControl, FieldType, FormBuilder, RequiredValidator} from "@ha/formhandler";
import {PasswordRuleValidator} from "../../validators/password-rule.validator";

/** The values the "Passwort ändern" form holds; the repetition never leaves the page. */
export type OwnPasswordFormData = {
    currentPassword: string,
    newPassword: string,
    repeatPassword: string,
}

const required = new RequiredValidator();

const meetsPasswordRule = new PasswordRuleValidator();

/** Builds the empty "Passwort ändern" form: current password, new password (rule S6), repetition. */
export function createOwnPasswordForm(): ComplexFormControl<OwnPasswordFormData, unknown> {
    return FormBuilder.buildForm('own-password', [
        {type: FieldType.Primitive, key: `currentPassword`, initialValue: ``, validations: [required]},
        {type: FieldType.Primitive, key: `newPassword`, initialValue: ``, validations: [required, meetsPasswordRule]},
        {type: FieldType.Primitive, key: `repeatPassword`, initialValue: ``, validations: [required]},
    ]);
}

/** Whether a typed new password misses rule S6 (no text while the field is still empty); read from the field's own validator. */
export function ownPasswordRuleNotMet(form: ComplexFormControl<OwnPasswordFormData, unknown>): boolean {
    const field = form.getField('newPassword');

    return (form.getValue()?.newPassword ?? '') !== '' && field !== null && field.getValidationErrors().length > 0;
}

/** Whether a typed repetition differs from the new password (no text while the repetition is still empty). */
export function ownPasswordRepeatDiffers(form: ComplexFormControl<OwnPasswordFormData, unknown>): boolean {
    const value = form.getValue();

    return (value?.repeatPassword ?? '') !== '' && value?.repeatPassword !== value?.newPassword;
}

/**
 * Whether the form may be saved: no field reports a validation error (every field is required, the
 * new password meets rule S6) and the repetition equals the new password. The repetition is compared here, not by a validator of the form itself: on a
 * form of fields @ha/formhandler's `isValid` checks only the form's own validators and
 * `getValidationErrors()` only the fields' (see `isOwnProfileFormSavable`), so a plain comparison is
 * the one check whose result is certain.
 */
export function isOwnPasswordFormSavable(form: ComplexFormControl<OwnPasswordFormData, unknown>): boolean {
    const value = form.getValue();

    return form.getValidationErrors().length === 0 && value?.repeatPassword === value?.newPassword;
}
